<template>
  <div class="period-block select-none" :class="{ bare }" :style="bare ? undefined : { background: bg }">
    <button
      v-if="!bare"
      @click="$emit('close')"
      class="w-full flex items-center justify-between"
      style="height: 32px; margin-bottom: 2px;"
    >
      <span class="leading-none text-ink" style="font-family: Inter, sans-serif; font-size: var(--fs-14); font-weight: 600;">
        Откуда {{ label }} за {{ days }} {{ dayWord(days) }}
      </span>
      <span
        class="leading-none flex items-center gap-1"
        style="font-family: Inter, sans-serif; font-size: var(--fs-12); font-weight: 400; color: var(--ink);"
      >свернуть <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg" style="transform: rotate(180deg);"><path d="M4 0.5L4 13.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/><path d="M0.5 10L4 13.5L7.5 10" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
    </button>

    <div v-if="items.length" class="flex flex-col" style="gap: 5px;">
      <div v-for="item in items" :key="item.name" class="line">
        <span class="name">{{ item.name }}</span>
        <!-- Сколько раз продукт встретился за период. На дне такой колонки быть
             не может, а здесь она отличает основу рациона от разовой покупки:
             одинаковая сумма набирается и десятью привычными порциями,
             и одной случайной. -->
        <span class="times">{{ item.times }} {{ timesWord(item.times) }}</span>
        <span class="value"><span v-if="item.has_estimate" class="approx-mark">~</span>{{ fmt(item.total) }}</span>
      </div>
    </div>

    <p v-else class="hint">{{ emptyText }}</p>

    <p v-if="rest.count > 0" class="hint">
      И ещё {{ rest.count }} {{ productWord(rest.count) }} — {{ fmt(rest.total) }}
    </p>
  </div>
</template>

<script setup>
import { computed } from 'vue'

// Рейтинг источников макронутриента за период. Обобщение бывшего
// FiberPeriodBlock: клетчатка и белок ходят одним компонентом, различаются
// только подписи, фон и поле *_breakdown из GET /api/diary/summary.
const props = defineProps({
  bare: { type: Boolean, default: false },
  // fiber_breakdown / protein_breakdown из GET /api/diary/summary
  breakdown: { type: Object, default: null },
  // Сколько дней охватывает период — идёт в заголовок, чтобы рамка была явной:
  // полоска показывает среднее за день, а список — суммы за весь период,
  // и без подписи числа выглядели бы несходящимися.
  days: { type: Number, required: true },
  // «клетчатка» / «белок» — в именительном для заголовка «Откуда …»
  label: { type: String, default: 'клетчатка' },
  emptyText: { type: String, default: 'За этот период клетчатки в дневнике нет.' },
  bg: { type: String, default: 'rgba(166, 183, 121, 0.2)' },
  // Ккал показываются целыми: доли калории не имеют смысла.
  integer: { type: Boolean, default: false },
  // Омега-3 нужны сотые: вне рыбы значения порядка 0.05–0.3
  decimals: { type: Number, default: null },
})

defineEmits(['close'])

const items = computed(() => props.breakdown?.items || [])
const rest = computed(() => ({
  count: props.breakdown?.rest_count || 0,
  total: props.breakdown?.rest_total || 0,
}))

function fmt(v) {
  if (props.integer) return Math.round(v)
  if (props.decimals != null) {
    const k = Math.pow(10, props.decimals)
    return Math.round(v * k) / k
  }
  return v >= 10 ? Math.round(v) : Math.round(v * 10) / 10
}

function plural(n, one, few, many) {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

const dayWord     = n => plural(n, 'день', 'дня', 'дней')
const timesWord   = n => plural(n, 'раз', 'раза', 'раз')
const productWord = n => plural(n, 'продукт', 'продукта', 'продуктов')
</script>

<style scoped>
/* Фон и нижнее скругление продолжают полоску MacroBar: тот же цвет заливки
   незаполненной части, поэтому вместе они читаются одной областью.
   Верхняя линия — ::before в потоке, а не border-top: горизонтальный паддинг
   блока укорачивает её до той же ширины, что у разделителей строк ниже —
   линия во всю ширину выбивалась из их ряда. Тон тот же. */
.period-block {
  padding: 0 10px 8px;
  border-radius: 0 0 12px 12px;
  margin-bottom: 6px;
  overflow: hidden;
}
.period-block::before {
  content: '';
  display: block;
  height: 1px;
  background: rgba(65, 41, 26, 0.09);
}
.period-block.bare { padding: 0; margin-bottom: 0; border-radius: 0; }
.period-block.bare::before { display: none; }
.line {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 7px 0;
  border-bottom: 1px solid rgba(65, 41, 26, 0.09);
}
.line:last-child { border-bottom: none; }
.name {
  font-family: Inter, sans-serif;
  font-size: var(--fs-14);
  font-weight: 500;
  color: var(--ink);
  line-height: 1.25;
  flex: 1 1 auto;
  min-width: 0;
}
.value {
  font-family: Inter, sans-serif;
  font-size: var(--fs-14);
  font-weight: 600;
  color: var(--ink);
  white-space: nowrap;
  min-width: 34px;
  text-align: right;
}
/* Знак «~» — общий класс .approx-mark в style.css. */
.times {
  font-family: Inter, sans-serif;
  font-size: var(--fs-12);
  font-weight: 400;
  color: var(--ink-70);
  white-space: nowrap;
}
.hint {
  font-family: Inter, sans-serif;
  font-size: var(--fs-12);
  font-weight: 400;
  color: var(--ink-70);
  line-height: 1.35;
  margin: 8px 0 0;
}
</style>
